import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { Periodo, RegistroDeAuditoria } from '@/lib/supabase/tipos-bd'
import { lunesDe } from '@/modules/tablero/semana'

/**
 * La bitácora de un periodo (fn_auditoria, 0249). Solo se lee: la tabla no
 * se deja editar ni borrar por nadie, y aquí no hay nada que lo intente.
 */
export function useAuditoria(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['auditoria', desde, hasta],
    queryFn: async (): Promise<RegistroDeAuditoria[]> => {
      const { data, error } = await supabase.rpc('fn_auditoria', {
        p_desde: desde,
        p_hasta: hasta,
      })
      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as RegistroDeAuditoria[]
    },
    // Lo nuevo entra solo cada minuto: es un registro que se está escribiendo.
    refetchInterval: 60_000,
    placeholderData: (previo) => previo,
  })
}

/**
 * Los bordes del periodo que contiene un día, con las reglas del tablero
 * (semana de lunes a domingo), para que «esta semana» sea la misma en todas
 * las pantallas.
 */
export function bordes(periodo: Periodo, dia: string): { desde: string; hasta: string } {
  const [a, m] = dia.split('-').map(Number)
  if (periodo === 'año') return { desde: `${a}-01-01`, hasta: `${a}-12-31` }
  if (periodo === 'mes') {
    const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate()
    const mm = String(m).padStart(2, '0')
    return { desde: `${a}-${mm}-01`, hasta: `${a}-${mm}-${String(ultimo).padStart(2, '0')}` }
  }
  const desde = lunesDe(dia)
  const fin = new Date(Date.parse(`${desde}T00:00:00Z`) + 6 * 86_400_000)
  return { desde, hasta: fin.toISOString().slice(0, 10) }
}

/** El día en Cartago de una marca de tiempo, como aaaa-mm-dd. */
export function diaEnCartago(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso))
}
